/**
 * HowTo schema definitions for every public calculator on /calculator/*.
 *
 * Rendered as schema.org HowTo JSON-LD inside the calculator pages. This
 * is the primary surface LLMs (ChatGPT, Perplexity, Claude) lean on when
 * answering "how do I calculate X" queries — having the answer in
 * HowTo-Step form makes the citation cleaner and the SERP rich result
 * (when Google still renders HowTo) more eligible.
 *
 * Keep steps:
 *  - 3 to 7 per calculator (Google's HowTo guidance — shorter is better)
 *  - imperative ("Measure", "Pick", "Enter") — matches LLM citation style
 *  - self-contained — each step text must read coherently on its own
 *  - concrete numbers wherever possible (LLMs cite numbers, skip vague prose)
 */

export interface CalculatorHowToStep {
  name: string;
  text: string;
}

export interface CalculatorHowTo {
  title: string;
  description: string;
  steps: CalculatorHowToStep[];
}

export const CALCULATOR_HOWTOS: Record<string, CalculatorHowTo> = {
  // ── Existing pre-Sprint-1 calculators ───────────────────────────────────
  fov: {
    title: 'How to calculate CCTV camera field of view',
    description:
      'Compute horizontal, vertical and diagonal field of view from focal length and sensor size per the standard FOV formula.',
    steps: [
      {
        name: 'Identify focal length',
        text: 'Read the focal length in millimetres from the camera datasheet. Typical CCTV values: 2.8, 4, 6, 8, 12, 25, 50 mm.',
      },
      {
        name: 'Identify sensor size',
        text: 'Read the sensor format (1/4", 1/3", 1/2.8", 1/2.7", 1/2.5", 1/1.8", 1") and convert to horizontal width in mm: 1/4" = 3.6 mm, 1/3" = 4.8 mm, 1/2.8" = 5.4 mm, 1/1.8" = 7.2 mm, 1" = 12.8 mm.',
      },
      {
        name: 'Apply the FOV formula',
        text: 'Compute HFOV = 2 × arctan(sensor_width / (2 × focal_length)). Multiply the result in radians by 180/π to get degrees.',
      },
      {
        name: 'Verify against datasheet',
        text: 'Compare the computed HFOV against the manufacturer-stated HFOV. A 5–10° gap is normal and explained by lens distortion and crop factor.',
      },
      {
        name: 'Project the cone',
        text: 'Multiply tan(HFOV/2) × 2 × distance to get coverage width at any target distance. Use this to verify the cone covers the protected area.',
      },
    ],
  },

  'lens-selector': {
    title: 'How to pick the right CCTV lens for a target distance',
    description:
      'Choose camera focal length based on target distance and required pixel density per EN 62676-4 DORI.',
    steps: [
      {
        name: 'Measure the target distance',
        text: 'Measure the distance from the planned camera position to the furthest point in the area you want to identify or recognise.',
      },
      {
        name: 'Pick the required DORI grade',
        text: 'Detect: 25 px/m. Observe: 62.5 px/m. Recognise: 125 px/m. Identify: 250 px/m. Pick the strictest grade the scene demands.',
      },
      {
        name: 'Enter camera resolution',
        text: 'Enter the camera sensor resolution in pixels (e.g. 1920×1080 for 2 MP, 2688×1520 for 4 MP, 3840×2160 for 8 MP).',
      },
      {
        name: 'Read the recommended focal length',
        text: 'The calculator returns the lens (2.8 / 4 / 6 / 12 / 25 / 50 mm) that meets your distance + DORI requirement. Two recommendations: wide-angle for overview, telephoto for detail.',
      },
    ],
  },

  dori: {
    title: 'How to calculate DORI zones per EN 62676-4',
    description:
      'Compute Detection, Observation, Recognition and Identification distances for a CCTV camera per EN 62676-4.',
    steps: [
      {
        name: 'Enter camera resolution',
        text: 'Enter horizontal pixel count from the datasheet (1920 for 2 MP, 2688 for 4 MP, 3840 for 8 MP, etc.).',
      },
      {
        name: 'Enter horizontal field of view',
        text: 'Enter HFOV in degrees from the datasheet or the FOV calculator. Typical: 2.8 mm = 100°, 4 mm = 80°, 6 mm = 55°.',
      },
      {
        name: 'Read the four DORI distances',
        text: 'Detection (25 px/m): the maximum range at which the camera detects presence. Observation (62.5 px/m): see general actions. Recognition (125 px/m): identify a known person. Identification (250 px/m): identify a stranger by face.',
      },
      {
        name: 'Verify against scene requirements',
        text: 'Plot the four zones on the floor plan. Targets at the Identification distance need face-grade pixel density; targets beyond fall to lower DORI grades.',
      },
    ],
  },

  oodpcvs: {
    title: 'How to apply the 7-step OODPCVS ladder per IEC 62676-4:2025',
    description:
      'Compute Overview, Outline, Discern, Perceive, Characterise, Validate and Scrutinise zones per the 2025 update to EN 62676-4.',
    steps: [
      {
        name: 'Map the legacy DORI zones',
        text: 'OODPCVS replaces DORI with a 7-step ladder: Overview 20, Outline 40, Discern 80, Perceive 125, Characterise 250, Validate 500, Scrutinise 1500 px/m.',
      },
      {
        name: 'Enter camera resolution and HFOV',
        text: 'Same inputs as DORI: camera horizontal pixel count and horizontal field of view in degrees.',
      },
      {
        name: 'Read the seven distances',
        text: 'The calculator returns the maximum range at which the camera reaches each OODPCVS grade. Validate (500 px/m) and Scrutinise (1500 px/m) are new thresholds for forensic-grade evidence.',
      },
      {
        name: 'Pick the controlling grade',
        text: 'For licence-plate cameras: Validate (500 px/m). For biometric face capture: Scrutinise (1500 px/m). For general identify: Characterise (250 px/m), unchanged from legacy DORI Identify.',
      },
    ],
  },

  ifov: {
    title: 'How to calculate instantaneous field of view (IFOV)',
    description:
      'Compute pixel angular resolution to assess how much real-world detail a single camera pixel covers.',
    steps: [
      {
        name: 'Enter HFOV',
        text: 'Enter the camera horizontal field of view in degrees (from datasheet or FOV calculator).',
      },
      {
        name: 'Enter horizontal pixel count',
        text: 'Enter the sensor horizontal resolution (e.g. 1920, 2688, 3840).',
      },
      {
        name: 'Compute IFOV',
        text: 'IFOV = HFOV / horizontal_pixels. Result is degrees per pixel. Smaller IFOV = finer angular resolution = better long-distance identification.',
      },
      {
        name: 'Convert to ground sample distance',
        text: 'At distance D metres, ground sample distance = D × tan(IFOV). This is the real-world size of one pixel and the limiting factor for licence-plate readability.',
      },
    ],
  },

  'pixel-density': {
    title: 'How to calculate pixels per metre at any distance',
    description:
      'Compute camera pixel density (px/m) at a target distance to verify DORI or OODPCVS compliance.',
    steps: [
      {
        name: 'Enter camera resolution',
        text: 'Enter horizontal pixel count from the camera datasheet.',
      },
      {
        name: 'Enter HFOV',
        text: 'Enter the horizontal field of view in degrees.',
      },
      {
        name: 'Enter target distance',
        text: 'Enter the distance in metres from camera to the area you want to measure.',
      },
      {
        name: 'Read pixel density',
        text: 'px/m = horizontal_pixels / (2 × distance × tan(HFOV/2)). Compare against DORI thresholds (25 / 62.5 / 125 / 250) or OODPCVS thresholds (20 / 40 / 80 / 125 / 250 / 500 / 1500).',
      },
    ],
  },

  ups: {
    title: 'How to size a UPS for an NVR + cameras + switches',
    description:
      'Compute the right UPS (battery backup) capacity for a CCTV system based on total wattage and runtime requirement.',
    steps: [
      {
        name: 'Sum recorder + switch wattage',
        text: 'Read the NVR power draw (typically 30–80 W) and PoE switch power (switch chassis 20–40 W plus PoE budget delivered).',
      },
      {
        name: 'Sum camera wattage',
        text: 'Camera draw: bullet 5–7 W, dome 4–6 W, PTZ 25–60 W, IR heater 10–15 W extra. Multiply per-camera draw by camera count.',
      },
      {
        name: 'Pick desired runtime',
        text: 'How long should the system survive a power cut? Typical: 15 min for short outages, 30–60 min for inverter handover, 4 h for unattended sites.',
      },
      {
        name: 'Read UPS VA rating',
        text: 'UPS_VA = total_W × 1.4 (typical inverter power factor). Pick a UPS where the rated runtime at this load exceeds your desired runtime.',
      },
    ],
  },

  'how-many-cameras': {
    title: 'How to calculate the number of cameras for a site',
    description:
      'Estimate the camera count for a perimeter or area based on coverage geometry.',
    steps: [
      {
        name: 'Enter site dimensions',
        text: 'For an area: length × width in metres. For a perimeter: total perimeter length.',
      },
      {
        name: 'Pick coverage type',
        text: 'Overview (one camera per 100–200 m²) vs identify-grade (one camera per 25–50 m²). Pick based on the strictest DORI requirement in the brief.',
      },
      {
        name: 'Add entry-point cameras',
        text: 'Add one camera per pedestrian door, one per vehicle gate, one per loading dock. These cameras are dedicated to face/plate capture and overlap the area cameras.',
      },
      {
        name: 'Read total count',
        text: 'Total = area_cameras + perimeter_cameras + entry_cameras + 10% spare. Use the visual planner to verify no blind spots remain.',
      },
    ],
  },

  // ── Sprint 1 calculators (new) ──────────────────────────────────────────
  'pir-sensors': {
    title: 'How to plan PIR sensor coverage for a room or perimeter',
    description:
      'Compute the number of PIR motion sensors needed for a room based on dimensions, mounting type and sensor model.',
    steps: [
      {
        name: 'Measure the room',
        text: 'Measure length × width in metres. Note ceiling height — high ceilings (>3.5 m) need ceiling-mount 360° PIRs, low ceilings need wall-mount wedge PIRs.',
      },
      {
        name: 'Count entrances',
        text: 'Count every door, sliding door and window that opens. Each entrance is a "choke point" — add one extra PIR per entrance to cover the immediate inside zone.',
      },
      {
        name: 'Pick sensor type',
        text: 'Standard PIR: cheap, prone to false alarms from pets. Dual-tech (PIR + microwave): required for EN 50131 Grade 3 sites. Curtain PIR: narrow 15 m × 2 m fan for window/door perimeter only.',
      },
      {
        name: 'Read recommended count',
        text: 'Wall-mount standard PIR covers ~50 m² after real-world derating. Ceiling-mount 360° covers ~105 m². The calculator adds one PIR per excess area block and one per entrance after the first.',
      },
      {
        name: 'Verify placement',
        text: 'Mount sensors so an intruder crosses the wedge perpendicular (5× more sensitive than head-on motion). Avoid HVAC vents, sunny windows, radiators and reflective surfaces.',
      },
    ],
  },

  'alarm-zones': {
    title: 'How to size alarm zones for EN 50131 Grade 2, Grade 3 or Grade 4',
    description:
      'Plan the number of alarm zones, sensor type and panel class for an intruder-alarm system per EN 50131.',
    steps: [
      {
        name: 'Pick the EN 50131 grade',
        text: 'Grade 2: commercial low-risk (shops, offices). Grade 3: banking, jewellery, pharma. Grade 4: military, casinos, high-cash. Higher grade means smaller zones and harder tamper resistance.',
      },
      {
        name: 'Enter site dimensions and openings',
        text: 'Enter site area in m², number of external doors, windows and skylights. Each external opening typically gets its own zone (Grade 2) or its own sensor pair (Grade 3+).',
      },
      {
        name: 'Read recommended zone count',
        text: 'Grade 2: ~10 detectors per zone, one zone per external opening. Grade 3: 1–3 detectors per zone, dual-tech on every area. Grade 4: single-detector zones with anti-mask + accelerometer on every sensor.',
      },
      {
        name: 'Pick panel class',
        text: 'Up to ~32 zones: DSC NEO, Paradox Magellan, Risco LightSYS. Up to ~100 zones: Galaxy Dimension, Pyronix Enforcer. 100+ zones: hybrid Lenel / Honeywell ProWatch with intrusion expansion modules.',
      },
      {
        name: 'Estimate cabling',
        text: 'Per-zone cable run: typically 30–80 m of 4-core alarm cable depending on layout. Multiply zone count × average run to budget total cable metres.',
      },
    ],
  },

  'beam-barrier': {
    title: 'How to plan active IR beam-barrier range with weather margin',
    description:
      'Compute effective range for an active infrared beam-barrier perimeter line accounting for weather derating and stacking.',
    steps: [
      {
        name: 'Pick beam barrier model',
        text: 'Specify the beam barrier model and its fair-weather rated range (e.g. Optex AX-200TF = 200 m, Takex PB-IN-100HF = 100 m).',
      },
      {
        name: 'Pick weather profile',
        text: 'Light rain / mist: × 0.75. Heavy fog or snow: × 0.40. Dust or sandstorm: × 0.50. Sub-zero icing: × 0.30. Pick the worst case the site will see.',
      },
      {
        name: 'Pick stack count (beam count)',
        text: '2-beam: minimum outdoor. 4-beam: standard commercial perimeter. 6-beam / 8-beam: high-security with cross-beam logic that defeats birds and falling leaves.',
      },
      {
        name: 'Read effective range',
        text: 'Effective range = rated_range × weather_factor. If the run exceeds effective range, either move receivers closer, add a relay tower, or stack two barriers back-to-back.',
      },
      {
        name: 'Plan beam alignment',
        text: 'Mount transmitter and receiver at the same height. Avoid line-of-sight obstructions (branches, banners). Add a small hood over the lens to prevent direct sun + snow icing.',
      },
    ],
  },

  'system-budget': {
    title: 'How to budget a complete CCTV / security system',
    description:
      'Estimate total project cost: cameras, recorder, switch, cabling, labour and optional access control + intrusion.',
    steps: [
      {
        name: 'Enter camera count and resolution',
        text: 'Enter number of cameras and average resolution (2 MP, 4 MP, 8 MP). Average prices: 2 MP €60–150, 4 MP €100–250, 8 MP €200–500 per camera (mid-range tier).',
      },
      {
        name: 'Pick NVR class',
        text: 'Hobby / SOHO: €150–300. Commercial: €400–1500 (channels × HDD bays × AI features). Enterprise / multi-site: €2000+ with redundant PSU and RAID.',
      },
      {
        name: 'Pick PoE switch class',
        text: '8-port unmanaged 802.3at: €80–150. 24-port managed 802.3at: €300–800. 48-port managed 802.3bt: €1200–2500. Match PoE budget to camera draw + 25% headroom.',
      },
      {
        name: 'Enter labour day-rate',
        text: 'Local installer day-rate. Typical EU: €350–600. UK: £400–700. US: $600–1200. Multiply by estimated install days (1 camera ≈ 0.5–1 hour for retrofit, faster for new-build).',
      },
      {
        name: 'Read total budget',
        text: 'Calculator returns equipment + cabling + labour with ±15% confidence bands. Add 10–20% project contingency for scope changes and on-site surprises.',
      },
    ],
  },

  'cable-length': {
    title: 'How to compute the maximum PoE cable length',
    description:
      'Calculate the maximum Cat5e / Cat6 / Cat6a cable length for a PoE camera without exceeding the 802.3af / at / bt voltage drop margin.',
    steps: [
      {
        name: 'Pick PoE class',
        text: '802.3af: 15.4 W at PSE, 12.95 W minimum at PD, 0.35 A max. 802.3at: 30 W / 25.5 W / 0.6 A. 802.3bt Type 3: 60 W / 51 W / 0.6 A. 802.3bt Type 4: 90 W / 71 W / 1.0 A.',
      },
      {
        name: 'Enter camera power draw',
        text: 'Read camera typical and peak power from the datasheet. Bullet: 5–7 W. Dome: 4–6 W. PTZ: 25–60 W. Add 10–15 W for IR heater / defroster cameras.',
      },
      {
        name: 'Pick cable type',
        text: 'Cat5e: 0.188 Ω/m (cheapest, lowest PoE budget). Cat6: 0.118 Ω/m (balanced). Cat6a: 0.094 Ω/m (longest PoE runs, supports 10 GbE). Solid-core stranded copper, not CCA (copper-clad aluminium).',
      },
      {
        name: 'Read maximum length',
        text: 'V_drop = I × R × length × 2 (round trip). The calculator returns the maximum length where PD voltage at the far end stays above the PoE class minimum.',
      },
      {
        name: 'Plan for the 100 m physical limit',
        text: 'Even if voltage allows more, Ethernet caps at 100 m per IEEE 802.3. For longer runs use a fibre + media converter, or a midspan PoE injector closer to the camera.',
      },
    ],
  },

  // Storage calculator landing (added 2026-05-24, see StorageCalculatorLanding.tsx).
  storage: {
    title: 'How to calculate CCTV storage requirements',
    description:
      'Compute total HDD capacity for a CCTV install from camera count, resolution, frame rate, codec, daily recording hours and retention days.',
    steps: [
      {
        name: 'List camera streams',
        text: 'Count cameras grouped by resolution (2 MP / 4 MP / 5 MP / 8 MP / 12 MP). Mixed installs need per-group sub-totals.',
      },
      {
        name: 'Pick the codec',
        text: 'H.264 baseline ≈ 4 Mbps at 1080p / 25 fps. H.265 cuts that in half. H.265+ / Smart Stream cuts another 30–40 % by adapting bitrate to scene activity. Default to H.265+ for any 2026 install where all cameras support it.',
      },
      {
        name: 'Apply the per-camera daily formula',
        text: 'GB per camera per day = bitrate_Mbps × 3600 × hours_per_day ÷ 8 ÷ 1024. Multiply by camera count and retention days to get total GB; divide by 1024 for TB.',
      },
      {
        name: 'Pick HDD size and RAID',
        text: 'For commercial installs, use surveillance-rated drives (WD Purple, Seagate SkyHawk, Toshiba S300) in RAID 5 (n+1 redundancy) or RAID 6 (n+2) for 8+ drive arrays.',
      },
    ],
  },

  // Bandwidth calculator landing (added 2026-05-24, see BandwidthCalculatorLanding.tsx).
  bandwidth: {
    title: 'How to calculate CCTV bandwidth requirements',
    description:
      'Compute total LAN throughput for a CCTV install from camera count, resolution, frame rate, codec and substream usage.',
    steps: [
      {
        name: 'Set per-camera bitrate',
        text: 'Use the codec reference: H.264 ≈ 4 Mbps at 1080p / 25 fps; H.265 ≈ 2 Mbps; H.265+ ≈ 1.2 Mbps. Scale linearly with resolution (×2 at 4 MP, ×4 at 4K) and fps (÷25 × target_fps).',
      },
      {
        name: 'Add substream',
        text: 'Most NVR clients pull a parallel low-resolution substream (~1 Mbps per camera) for grid view and mobile. Add this to the per-camera total for accurate network sizing.',
      },
      {
        name: 'Sum and apply headroom',
        text: 'Total_Mbps = (main + substream) × camera_count. Add 20 % headroom for motion-event spikes. Compare against switch uplink speed.',
      },
      {
        name: 'Pick switch + NVR network class',
        text: 'Under 80 Mbps: Fast Ethernet OK. 80–800 Mbps: managed 1 Gbps PoE switch. Over 800 Mbps: 10 Gbps SFP+ uplink between switch and NVR. Always VLAN-separate camera traffic from corporate LAN.',
      },
    ],
  },

  // PoE budget calculator (added 2026-05-24, see PoECalculator.tsx).
  // Distinct from 'cable-length' above: that focuses on max-distance for a
  // single run; this one sizes the switch and class for a whole install.
  poe: {
    title: 'How to size a PoE switch budget for a CCTV install',
    description:
      'Compute the IEEE 802.3 PoE class per camera, the total switch power budget with 20% headroom, and verify the longest Cat5e run stays within voltage-drop tolerance.',
    steps: [
      {
        name: 'List every powered device',
        text: 'Count cameras by power draw (W at the PD). Include any PoE NVR or recorder, PoE access points, and PoE intercom. Exclude devices powered by 12 V DC injectors.',
      },
      {
        name: 'Pick the PoE class per device',
        text: 'Match camera PD wattage to the lowest class that covers it. 802.3af (PoE, 12.95 W) for fixed-lens cameras. 802.3at (PoE+, 25.5 W) for IR bullets and vandal domes. 802.3bt Type 3 (51 W) for PTZ. Type 4 (71 W) for heated multi-sensor cameras.',
      },
      {
        name: 'Sum the source-side wattage',
        text: 'Each device draws source_W = class_max_W at the PSE port. Sum across all devices, then add 20 % headroom for startup spikes and future expansion. The result is the minimum switch power budget.',
      },
      {
        name: 'Pick a managed PoE switch',
        text: 'Look for the line "PoE Power Budget: X W" on the switch spec sheet, NOT the per-port maximum. Pick a switch where total budget ≥ your computed requirement and port count ≥ devices + 2 spare. PoE+ rated switch for 802.3at installs; PoE++ for 802.3bt.',
      },
      {
        name: 'Verify the longest cable run',
        text: 'IEEE PoE specifies a 100 m maximum on Cat5e. Voltage drop scales linearly with length and inversely with conductor count (2-pair af/at vs 4-pair bt). Past 100 m, switch to Cat6 (lower R), add a PoE extender, or convert to fibre with a media converter near the camera.',
      },
    ],
  },
};
